/**
 * ⚠️  INTERNAL — /admin/gallery
 * Manage gallery items: upload, edit, reorder (drag-and-drop), delete.
 */
import React, { useState, useEffect, useRef, useCallback, DragEvent } from 'react';
import { AdminLoginGate, isAdminAuthed, clearAdminAuth } from '../../components/AdminLoginGate';
import { getAdminToken } from '../../lib/image-slots-client';
import {
  adminFetchGalleryItems,
  adminCreateGalleryItem,
  adminUpdateGalleryItem,
  adminDeleteGalleryItem,
  adminReorderGalleryItems,
  adminGetGalleryUploadUrl,
  type GalleryItem,
} from '../../lib/gallery-client';

// ── Icons ──────────────────────────────────────────────────────────────────────
const Icon = {
  Plus:    () => <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/></svg>,
  Edit:    () => <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536M4 16.5V20h3.5l10.06-10.06-3.5-3.5L4 16.5z"/></svg>,
  Trash:   () => <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2M10 11v6M14 11v6M5 7l1 13a1 1 0 001 1h10a1 1 0 001-1l1-13"/></svg>,
  Grip:    () => <svg className="h-4 w-4 flex-shrink-0 cursor-grab active:cursor-grabbing" fill="currentColor" viewBox="0 0 24 24"><circle cx="9" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="15" cy="18" r="1.5"/></svg>,
  X:       () => <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>,
  Check:   () => <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>,
  Upload:  () => <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M16 12l-4-4-4 4M12 8v8"/></svg>,
  Eye:     () => <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  EyeOff:  () => <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22"/></svg>,
  Refresh: () => <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>,
};

const ALLOWED_MIME = new Set(['image/jpeg', 'image/png', 'image/webp']);

type Category = 'repairs' | 'controllers' | 'strange';

const CATEGORY_LABELS: Record<Category, string> = {
  repairs: 'Repairs (Before & After)',
  controllers: 'Custom Controllers',
  strange: 'Strange Finds',
};

// ── Image upload helper ────────────────────────────────────────────────────────

async function uploadImage(token: string, file: File): Promise<{ publicUrl: string; storagePath: string }> {
  const { signedUrl, path, publicUrl } = await adminGetGalleryUploadUrl(token, file.type);
  const res = await fetch(signedUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
  if (!res.ok) throw new Error('Upload to storage failed');
  return { publicUrl, storagePath: path };
}

// ── Blank form factory ─────────────────────────────────────────────────────────

function blankForm(category: Category): Partial<GalleryItem> {
  return {
    category,
    title: '',
    caption: '',
    subtitle: '',
    image_url: '',
    secondary_image_url: '',
    storage_path: null,
    secondary_storage_path: null,
    mime_type: 'image/jpeg',
    sort_order: 0,
    visible: true,
    featured: false,
  };
}

// ── ImageUploadBox ─────────────────────────────────────────────────────────────

function ImageUploadBox({
  label,
  previewUrl,
  onFile,
  uploading,
  disabled,
}: {
  label: string;
  previewUrl: string;
  onFile: (file: File) => void;
  uploading: boolean;
  disabled?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="space-y-1">
      <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{label}</p>
      <div
        onClick={() => !disabled && !uploading && ref.current?.click()}
        className={`relative flex items-center justify-center bg-black/30 border-2 border-dashed rounded-lg overflow-hidden cursor-pointer transition-colors
          ${disabled ? 'opacity-40 cursor-not-allowed border-gray-800' : 'border-gray-700 hover:border-gray-500'}`}
        style={{ height: 140 }}
      >
        {previewUrl
          ? <img src={previewUrl} alt={label} className="w-full h-full object-cover" />
          : <div className="flex flex-col items-center gap-1 text-gray-600">
              <Icon.Upload />
              <span className="text-xs">{uploading ? 'Uploading…' : 'Click to upload'}</span>
            </div>
        }
        {uploading && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-sm">
            Uploading…
          </div>
        )}
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        disabled={disabled || uploading}
        onChange={e => { const f = e.target.files?.[0]; if (f) { onFile(f); e.target.value = ''; } }}
      />
    </div>
  );
}

// ── AddItemPanel ───────────────────────────────────────────────────────────────

function AddItemPanel({
  category,
  token,
  onCreated,
  onCancel,
}: {
  category: Category;
  token: string;
  onCreated: (item: GalleryItem) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<Partial<GalleryItem>>(blankForm(category));
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingBefore, setUploadingBefore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleMainFile = async (file: File) => {
    if (!ALLOWED_MIME.has(file.type)) { setErr('Only JPG, PNG, WebP allowed'); return; }
    setErr(null);
    setUploadingMain(true);
    try {
      const { publicUrl, storagePath } = await uploadImage(token, file);
      setForm(f => ({ ...f, image_url: publicUrl, storage_path: storagePath, mime_type: file.type }));
    } catch (e: unknown) { setErr((e as Error).message); }
    finally { setUploadingMain(false); }
  };

  const handleBeforeFile = async (file: File) => {
    if (!ALLOWED_MIME.has(file.type)) { setErr('Only JPG, PNG, WebP allowed'); return; }
    setErr(null);
    setUploadingBefore(true);
    try {
      const { publicUrl, storagePath } = await uploadImage(token, file);
      setForm(f => ({ ...f, secondary_image_url: publicUrl, secondary_storage_path: storagePath }));
    } catch (e: unknown) { setErr((e as Error).message); }
    finally { setUploadingBefore(false); }
  };

  const handleSave = async () => {
    if (!form.title?.trim()) { setErr('Title is required'); return; }
    setSaving(true);
    setErr(null);
    try {
      const created = await adminCreateGalleryItem(token, form);
      onCreated(created);
    } catch (e: unknown) { setErr((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 mb-6 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-white font-semibold">New {CATEGORY_LABELS[category]} Item</h3>
        <button onClick={onCancel} className="text-gray-500 hover:text-white"><Icon.X /></button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <ImageUploadBox
          label={category === 'repairs' ? 'After Image' : 'Photo'}
          previewUrl={form.image_url ?? ''}
          onFile={handleMainFile}
          uploading={uploadingMain}
        />
        {category === 'repairs' && (
          <ImageUploadBox
            label="Before Image"
            previewUrl={form.secondary_image_url ?? ''}
            onFile={handleBeforeFile}
            uploading={uploadingBefore}
          />
        )}
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs text-gray-400 block mb-1">Title *</label>
          <input
            value={form.title ?? ''}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="e.g. Iron Man Edition"
            className="w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-gray-500"
          />
        </div>
        {category === 'repairs' && (
          <div>
            <label className="text-xs text-gray-400 block mb-1">Issue Tag (subtitle)</label>
            <input
              value={form.subtitle ?? ''}
              onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}
              placeholder="e.g. Overheating - APU Oxidation"
              className="w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-gray-500"
            />
          </div>
        )}
        <div>
          <label className="text-xs text-gray-400 block mb-1">Description</label>
          <textarea
            value={form.caption ?? ''}
            onChange={e => setForm(f => ({ ...f, caption: e.target.value }))}
            rows={3}
            placeholder="Describe this item…"
            className="w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-gray-500 resize-none"
          />
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-400">
            <input
              type="checkbox"
              checked={form.visible ?? true}
              onChange={e => setForm(f => ({ ...f, visible: e.target.checked }))}
              className="accent-[#39FF14] w-4 h-4"
            />
            Visible on public gallery
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-400">
            <input
              type="checkbox"
              checked={form.featured ?? false}
              onChange={e => setForm(f => ({ ...f, featured: e.target.checked }))}
              className="accent-yellow-400 w-4 h-4"
            />
            Featured
          </label>
        </div>
      </div>

      {err && <p className="text-red-400 text-sm">{err}</p>}

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">Cancel</button>
        <button
          onClick={handleSave}
          disabled={saving || uploadingMain || uploadingBefore}
          className="px-4 py-2 text-sm bg-[#39FF14] hover:bg-[#00BFFF] text-black font-semibold rounded-lg transition-colors disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save Item'}
        </button>
      </div>
    </div>
  );
}

// ── ItemRow ────────────────────────────────────────────────────────────────────

function ItemRow({
  item,
  token,
  onUpdated,
  onDeleted,
  dragHandlers,
}: {
  item: GalleryItem;
  token: string;
  onUpdated: (item: GalleryItem) => void;
  onDeleted: (id: string) => void;
  dragHandlers: {
    onDragStart: (e: DragEvent<HTMLDivElement>, id: string) => void;
    onDragOver:  (e: DragEvent<HTMLDivElement>, id: string) => void;
    onDrop:      (e: DragEvent<HTMLDivElement>) => void;
    isDragOver:  boolean;
  };
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: item.title, caption: item.caption, subtitle: item.subtitle });
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingBefore, setUploadingBefore] = useState(false);
  const [mainUrl, setMainUrl] = useState(item.image_url);
  const [beforeUrl, setBeforeUrl] = useState(item.secondary_image_url);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleToggleVisible = async () => {
    try {
      const updated = await adminUpdateGalleryItem(token, item.id, { visible: !item.visible });
      onUpdated(updated);
    } catch (e: unknown) { setErr((e as Error).message); }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    setErr(null);
    try {
      const updated = await adminUpdateGalleryItem(token, item.id, {
        title: form.title,
        caption: form.caption,
        subtitle: form.subtitle,
        image_url: mainUrl,
        secondary_image_url: beforeUrl,
      });
      onUpdated(updated);
      setEditing(false);
    } catch (e: unknown) { setErr((e as Error).message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await adminDeleteGalleryItem(token, item.id);
      onDeleted(item.id);
    } catch (e: unknown) { setErr((e as Error).message); }
  };

  const handleMainFile = async (file: File) => {
    setUploadingMain(true);
    try {
      const { publicUrl } = await uploadImage(token, file);
      setMainUrl(publicUrl);
    } catch (e: unknown) { setErr((e as Error).message); }
    finally { setUploadingMain(false); }
  };

  const handleBeforeFile = async (file: File) => {
    setUploadingBefore(true);
    try {
      const { publicUrl } = await uploadImage(token, file);
      setBeforeUrl(publicUrl);
    } catch (e: unknown) { setErr((e as Error).message); }
    finally { setUploadingBefore(false); }
  };

  return (
    <div
      draggable
      onDragStart={e => dragHandlers.onDragStart(e, item.id)}
      onDragOver={e => dragHandlers.onDragOver(e, item.id)}
      onDrop={dragHandlers.onDrop}
      className={`bg-gray-900/60 border rounded-xl overflow-hidden transition-all
        ${dragHandlers.isDragOver ? 'border-[#39FF14]/60 scale-[1.01]' : 'border-gray-800'}`}
    >
      {/* Row header */}
      <div className="flex items-center gap-3 p-3">
        <span className="text-gray-600 hover:text-gray-400"><Icon.Grip /></span>
        <div className="h-12 w-12 rounded-lg overflow-hidden flex-shrink-0 bg-black/40">
          {mainUrl
            ? <img src={mainUrl} alt={item.title} className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center text-gray-700 text-xs">—</div>
          }
        </div>
        <div className="flex-1 min-w-0">
          {editing
            ? <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className="w-full bg-black/40 border border-gray-700 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-gray-500"
              />
            : <p className="text-sm text-white font-medium truncate">{item.title}</p>
          }
          {item.subtitle && !editing && (
            <p className="text-xs text-gray-500 truncate">{item.subtitle}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs px-2 py-0.5 rounded-full ${item.visible ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-500'}`}>
            {item.visible ? 'Visible' : 'Hidden'}
          </span>
          <button onClick={handleToggleVisible} title={item.visible ? 'Hide' : 'Show'}
            className="text-gray-500 hover:text-white p-1 rounded transition-colors">
            {item.visible ? <Icon.Eye /> : <Icon.EyeOff />}
          </button>
          <button onClick={() => { setEditing(e => !e); setForm({ title: item.title, caption: item.caption, subtitle: item.subtitle }); setMainUrl(item.image_url); setBeforeUrl(item.secondary_image_url); }}
            className="text-gray-500 hover:text-blue-400 p-1 rounded transition-colors">
            <Icon.Edit />
          </button>
          <button onClick={handleDelete} className="text-gray-500 hover:text-red-400 p-1 rounded transition-colors">
            <Icon.Trash />
          </button>
        </div>
      </div>

      {/* Expanded edit form */}
      {editing && (
        <div className="border-t border-gray-800 p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <ImageUploadBox
              label={item.category === 'repairs' ? 'After Image' : 'Photo'}
              previewUrl={mainUrl}
              onFile={handleMainFile}
              uploading={uploadingMain}
            />
            {item.category === 'repairs' && (
              <ImageUploadBox
                label="Before Image"
                previewUrl={beforeUrl}
                onFile={handleBeforeFile}
                uploading={uploadingBefore}
              />
            )}
          </div>
          <div className="space-y-2">
            {item.category === 'repairs' && (
              <div>
                <label className="text-xs text-gray-400 block mb-1">Issue Tag</label>
                <input
                  value={form.subtitle}
                  onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}
                  placeholder="e.g. Overheating - APU Oxidation"
                  className="w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-gray-500"
                />
              </div>
            )}
            <div>
              <label className="text-xs text-gray-400 block mb-1">Description</label>
              <textarea
                value={form.caption}
                onChange={e => setForm(f => ({ ...f, caption: e.target.value }))}
                rows={3}
                className="w-full bg-black/40 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-gray-500 resize-none"
              />
            </div>
          </div>
          {err && <p className="text-red-400 text-sm">{err}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button
              onClick={handleSaveEdit}
              disabled={saving || uploadingMain || uploadingBefore}
              className="px-4 py-1.5 text-sm bg-[#39FF14] hover:bg-[#00BFFF] text-black font-semibold rounded-lg transition-colors disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── CategoryPanel ──────────────────────────────────────────────────────────────

function CategoryPanel({
  category,
  items,
  token,
  onItemsChanged,
}: {
  category: Category;
  items: GalleryItem[];
  token: string;
  onItemsChanged: (items: GalleryItem[]) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  const categoryItems = items
    .filter(i => i.category === category)
    .sort((a, b) => a.sort_order - b.sort_order);

  const handleCreated = (item: GalleryItem) => {
    onItemsChanged([...items, item]);
    setAdding(false);
  };

  const handleUpdated = (updated: GalleryItem) => {
    onItemsChanged(items.map(i => i.id === updated.id ? updated : i));
  };

  const handleDeleted = (id: string) => {
    onItemsChanged(items.filter(i => i.id !== id));
  };

  // Drag & drop reorder
  const onDragStart = useCallback((e: DragEvent<HTMLDivElement>, id: string) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const onDragOver = useCallback((e: DragEvent<HTMLDivElement>, id: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverId(id);
  }, []);

  const onDrop = useCallback(async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!dragId || !dragOverId || dragId === dragOverId) { setDragId(null); setDragOverId(null); return; }

    const ids = categoryItems.map(i => i.id);
    const from = ids.indexOf(dragId);
    const to   = ids.indexOf(dragOverId);
    if (from === -1 || to === -1) { setDragId(null); setDragOverId(null); return; }

    const reordered = [...categoryItems];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);

    const withNewOrder = reordered.map((item, idx) => ({ ...item, sort_order: idx }));
    onItemsChanged(items.map(i => {
      const found = withNewOrder.find(r => r.id === i.id);
      return found ?? i;
    }));

    setDragId(null);
    setDragOverId(null);

    setReordering(true);
    try {
      const token_ = getAdminToken() ?? token;
      await adminReorderGalleryItems(token_, withNewOrder.map(i => ({ id: i.id, sort_order: i.sort_order })));
    } catch { /* non-fatal */ }
    finally { setReordering(false); }
  }, [dragId, dragOverId, categoryItems, items, onItemsChanged, token]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white">{CATEGORY_LABELS[category]}</h2>
        <div className="flex items-center gap-3">
          {reordering && <span className="text-xs text-gray-500 animate-pulse">Saving order…</span>}
          <button
            onClick={() => setAdding(a => !a)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#39FF14] hover:bg-[#00BFFF] text-black font-semibold rounded-lg transition-colors"
          >
            <Icon.Plus />
            Add Photo
          </button>
        </div>
      </div>

      {adding && (
        <AddItemPanel
          category={category}
          token={token}
          onCreated={handleCreated}
          onCancel={() => setAdding(false)}
        />
      )}

      <div className="space-y-2">
        {categoryItems.length === 0 && (
          <p className="text-gray-600 text-sm py-6 text-center">No items yet. Click "Add Photo" to get started.</p>
        )}
        {categoryItems.map(item => (
          <ItemRow
            key={item.id}
            item={item}
            token={token}
            onUpdated={handleUpdated}
            onDeleted={handleDeleted}
            dragHandlers={{
              onDragStart,
              onDragOver,
              onDrop,
              isDragOver: dragOverId === item.id,
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function AdminGalleryPage() {
  const [authed, setAuthed] = useState(isAdminAuthed());
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Category>('repairs');

  const token = getAdminToken() ?? '';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminFetchGalleryItems(token);
      setItems(data);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  if (!authed) {
    return <AdminLoginGate onAuth={() => setAuthed(true)} />;
  }

  const TAB_ACTIVE = 'bg-gray-700 text-white';
  const TAB_IDLE   = 'text-gray-500 hover:text-white';

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0]">
      {/* Header bar */}
      <div className="border-b border-gray-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-white">Gallery Manager</h1>
          {loading && <span className="text-xs text-gray-500 animate-pulse">Loading…</span>}
        </div>
        <div className="flex items-center gap-3">
          <a href="/gallery" target="_blank" rel="noopener noreferrer"
            className="text-xs text-gray-500 hover:text-[#39FF14] transition-colors">
            View public gallery ↗
          </a>
          <button onClick={load} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors">
            <Icon.Refresh /><span>Refresh</span>
          </button>
          <button onClick={() => { clearAdminAuth(); setAuthed(false); }}
            className="text-sm text-gray-500 hover:text-red-400 transition-colors">
            Sign out
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-800 rounded-xl text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Category tabs */}
        <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-xl p-1 mb-8 w-fit">
          {(['repairs', 'controllers', 'strange'] as Category[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === tab ? TAB_ACTIVE : TAB_IDLE}`}
            >
              {tab === 'repairs' ? 'Repairs' : tab === 'controllers' ? 'Controllers' : 'Strange'}
              <span className="ml-2 text-xs text-gray-600">
                {items.filter(i => i.category === tab).length}
              </span>
            </button>
          ))}
        </div>

        <CategoryPanel
          key={activeTab}
          category={activeTab}
          items={items}
          token={token}
          onItemsChanged={setItems}
        />
      </div>
    </div>
  );
}
